/**
 * §5 — Player roles + per-token instruction sets.
 *
 * Coaches can specialise a token within its formation slot — a CB
 * becomes a stopper / ball-playing / wide-CB / sweeper; a CM becomes
 * a deep-lying playmaker / box-to-box / mezzala / ball-winner / shadow-
 * striker — without rewiring the formation itself. The role is purely
 * a coaching label here; the rendering layer can read it to pick an
 * archetype mesh / animation, but it doesn't drive positioning.
 *
 * Instructions are a small bag of enums + one optional opponent
 * pointer (`markTarget`) — kept deliberately shallow so the HUD shows
 * roughly five toggles per token instead of thirty.
 *
 * Storage lives under `TacticsLayout.playerRoles` (see ./types.ts) as
 * `Record<'home-3' | 'away-7' | ..., PlayerRoleSelection>`. Old layouts
 * (v < 4) leave the slot undefined; readers must default per-slot via
 * `defaultRoleSelectionForSlot`.
 */

/** A starter vocabulary mapping slot codes to ordered role identifiers.
 *  The first entry is treated as the default for that slot. Treat the
 *  list as open — clubs may extend it later — but every slot we use in
 *  v1 (`GK`, `CB`, `FB`, `CM`, `DM`, `AM`, `W`, `IF`, `ST`) appears
 *  here so `defaultRoleSelectionForSlot` always lands on something
 *  sensible. */
export const ROLE_VOCABULARY: Record<string, readonly string[]> = {
  GK: ['sweeper-keeper', 'shot-stopper', 'distributor'],
  CB: ['stopper', 'ball-playing', 'wide-cb', 'sweeper'],
  FB: ['full-back', 'wing-back', 'inverted-full-back', 'overlapping-full-back'],
  DM: ['holding-midfielder', 'deep-lying-playmaker', 'anchor', 'ball-winner'],
  CM: [
    'box-to-box',
    'deep-lying-playmaker',
    'mezzala',
    'ball-winner',
    'shadow-striker',
  ],
  AM: ['advanced-playmaker', 'shadow-striker', 'trequartista'],
  W: ['pure-winger', 'wide-playmaker', 'inside-forward'],
  IF: ['inside-forward', 'wide-playmaker', 'pure-winger'],
  ST: ['target', 'poacher', 'false-9', 'pressing-forward', 'complete-forward'],
};

/** Per-instruction field definition — shape `{ key, label, values, default }`.
 *  `values[0]` isn't necessarily the default — `default` is explicit so
 *  the UI can render values in coaching-natural order (off / balanced /
 *  high) while the default sits in the middle. */
export interface InstructionFieldDef {
  /** Storage key under `PlayerRoleSelection.instructions`. */
  key: string;
  /** Human-readable label for the chip row. */
  label: string;
  /** Allowed enum values in display order. */
  values: readonly string[];
  /** Default value applied by `defaultRoleSelectionForSlot`. */
  default: string;
}

export const INSTRUCTION_FIELDS: readonly InstructionFieldDef[] = [
  {
    key: 'attacking_intent',
    label: 'Attacking intent',
    values: ['off', 'balanced', 'high'],
    default: 'balanced',
  },
  {
    key: 'defensive_intent',
    label: 'Defensive intent',
    values: ['off', 'balanced', 'high'],
    default: 'balanced',
  },
  {
    key: 'press_trigger',
    label: 'Press trigger',
    values: ['none', 'passive', 'aggressive'],
    default: 'passive',
  },
  {
    key: 'width',
    label: 'Width',
    values: ['narrow', 'balanced', 'wide'],
    default: 'balanced',
  },
  {
    key: 'positioning',
    label: 'Positioning',
    values: ['stay', 'drift', 'free-roam'],
    default: 'stay',
  },
];

/** Pointer field — opponent token key the player is asked to mark.
 *  Stored separately so the picker can render it as a dropdown sourced
 *  from the actual opponent roster, not a fixed enum. */
export const MARK_TARGET_FIELD = {
  key: 'mark_target',
  label: 'Mark target',
  /** Convention: `away-<index>` when the home team is marking, etc. */
  format: 'team-index',
  default: null as string | null,
} as const;

/** A single player's coaching selection. */
export interface PlayerRoleSelection {
  /** Role identifier from `ROLE_VOCABULARY[slot]` — e.g. 'stopper'. */
  role: string;
  /** Instruction enums keyed by `INSTRUCTION_FIELDS[].key`. Every field
   *  is required at write time (defaults filled by
   *  `defaultRoleSelectionForSlot`) so the renderer never has to
   *  branch on `undefined`. */
  instructions: Record<string, string>;
  /** Optional opponent-token key — e.g. 'away-9'. */
  markTarget?: string;
}

/** Storage shape — keyed by `team-index` (matches arrow.playerKey /
 *  voiceover.keyframeId convention). */
export type TacticsPlayerRoles = Record<string, PlayerRoleSelection>;

/** Build a fully-populated default selection for the given slot. Falls
 *  back to a neutral selection when the slot is unknown so legacy
 *  layouts loaded against a stale vocabulary still get sensible
 *  defaults. */
export function defaultRoleSelectionForSlot(
  slot: string,
): PlayerRoleSelection {
  const roles = ROLE_VOCABULARY[slot] ?? ['generic'];
  const instructions: Record<string, string> = {};
  for (const field of INSTRUCTION_FIELDS) {
    instructions[field.key] = field.default;
  }
  return {
    role: roles[0],
    instructions,
  };
}

/** Heuristic — given a role identifier (e.g. 'ball-playing') return the
 *  slot code it belongs to (`'CB'`). Used at load time to sanity-check
 *  stored selections against the current vocabulary, and by HUDs that
 *  show the slot header without keeping the original slot on the
 *  selection object. Returns `null` if no slot owns the role. */
export function inferSlotFromRoleString(role: string): string | null {
  for (const [slot, roles] of Object.entries(ROLE_VOCABULARY)) {
    if (roles.includes(role)) return slot;
  }
  return null;
}

/** Type guard for `PlayerRoleSelection`. Accepts the minimum viable
 *  shape: a non-empty `role` string plus an `instructions` record of
 *  string→string. `markTarget` is optional but, when present, must be
 *  a string. */
export function isPlayerRoleSelection(
  value: unknown,
): value is PlayerRoleSelection {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.role !== 'string' || v.role.length === 0) return false;
  if (!v.instructions || typeof v.instructions !== 'object') return false;
  for (const entry of Object.values(v.instructions)) {
    if (typeof entry !== 'string') return false;
  }
  if (v.markTarget !== undefined && typeof v.markTarget !== 'string') {
    return false;
  }
  return true;
}
